import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Star, Loader2 } from "lucide-react";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { resolveAssetUrl } from "@/components/ImageUploader";

// Same Unsplash fallbacks used in Recipes.jsx
const METHOD_IMG = {
  v60: "https://images.unsplash.com/photo-1545665225-b23b99e4d45e?auto=format&fit=crop&w=600&q=80",
  chemex: "https://images.unsplash.com/photo-1497935586351-b67a49e012bf?auto=format&fit=crop&w=600&q=80",
  espresso: "https://images.unsplash.com/photo-1510707577719-ae7c14805e3a?auto=format&fit=crop&w=600&q=80",
  frenchpress: "https://images.unsplash.com/photo-1485808191679-5f86510681a2?auto=format&fit=crop&w=600&q=80",
  aeropress: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=600&q=80",
  moka: "https://images.unsplash.com/photo-1510707577719-ae7c14805e3a?auto=format&fit=crop&w=600&q=80",
  coldbrew: "https://images.unsplash.com/photo-1517701604599-bb29b565090c?auto=format&fit=crop&w=600&q=80",
  turkish: "https://images.unsplash.com/photo-1559925393-8be0ec4767c8?auto=format&fit=crop&w=600&q=80",
};
const imgOf = (r) => resolveAssetUrl(r?.image_url) || METHOD_IMG[(r?.method || "").toLowerCase()] || null;

export default function SimilarRecipes({ recipeId, onPick }) {
  const { lang, isRTL } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ["similar-recipes", recipeId],
    queryFn: () => endpoints.similarRecipes(recipeId),
    enabled: !!recipeId,
    staleTime: 5 * 60 * 1000,
  });
  const recipes = data?.recipes || [];

  return (
    <div className="pt-2" data-testid="similar-recipes">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-[#C86240]" />
        <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E]">
          {isRTL ? "وصفات شبيهة مقترحة" : "Similar recipes (AI)"}
        </div>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-[#5C544F]">
          <Loader2 className="w-3 h-3 animate-spin" />
          {isRTL ? "يبحث عن وصفات شبيهة..." : "Finding similar recipes..."}
        </div>
      ) : recipes.length === 0 ? (
        <div className="text-xs text-[#5C544F]">
          {isRTL ? "لا توجد وصفات شبيهة بعد." : "No similar recipes yet."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {recipes.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => onPick?.(r)}
              data-testid={`similar-recipe-${r.id}`}
              className="text-start rounded-xl bg-white border border-[#2A2421]/10 overflow-hidden hover:border-[#C86240]/40 hover:-translate-y-0.5 transition-all"
            >
              <div className="aspect-[16/10] bg-[#F9F6F0] overflow-hidden">
                {imgOf(r) ? (
                  <img src={imgOf(r)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#C86240]/30">
                    <Star className="w-6 h-6" />
                  </div>
                )}
              </div>
              <div className="p-2.5">
                <div className="text-[9px] uppercase tracking-[0.2em] text-[#4A5D4E]">{r.method}</div>
                <div className="text-sm font-semibold text-[#2A2421] leading-snug line-clamp-2 mt-0.5">
                  {lang === "ar" ? r.title_ar : r.title_en}
                </div>
                {r.ratings_count > 0 && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-[#C86240]">
                    <Star className="w-2.5 h-2.5 fill-[#C86240]" />
                    <span className="font-semibold">{(r.avg_rating || 0).toFixed(1)}</span>
                    <span className="text-[#5C544F]">({r.ratings_count})</span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
